<script lang="ts">
  import SolanaShell from "../../components/SolanaShell.svelte";
  import Stats from "../../components/Stats.svelte";
  import { PlayIcon, OctagonPauseIcon, ClockIcon } from "lucide-svelte";
  import CurvePools from "../../components/CurvePools.svelte";
  import LiveTrades from "../../components/LiveTrades.svelte";
  import TopPools from "../../components/TopPools.svelte";
  import WalletBubbles from "../../components/WalletBubbles.svelte";
  import PoolView from "../../components/PoolView.svelte";
  import { STORE, initStore } from "../../../state.svelte";
  import { onDestroy, onMount } from "svelte";

  let ready = $state(false);

  onMount(async () => {
    await initStore();
    ready = true;
  });

  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/tools/solana"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Tools", url: "https://0xfire.com/tools" },
      { name: "Solana", url: "https://0xfire.com/tools/solana" },
    ]),
  ));

  const IDLE_LIMIT = 60_000;
  const BUY_URL = "https://cloud.solanavibestation.com/index.php?/cart/sailfish-api---streamer/";

  const LIVE_TABS = ["Live Trades", "Top Pools", "Curve Pools", "Wallets", "Pool View"];
  let activeTab = $state(LIVE_TABS[0]);
  let selectedPool = $state("");
  let isRunning = $state(false);
  let showIdleModal = $state(false);
  let idleTimer: any;

  const openPoolView = (poolAddress: string) => {
    selectedPool = poolAddress;
    activeTab = "Pool View";
  };

  $effect(() => {
    isRunning = STORE.bot.sailfish.isRunning();
  });

  let hasData = $derived(STORE.bot.totalTradeCount > 0 || Object.keys(STORE.bot.poolsStats).length > 0);

  function resetIdleTimer() {
    clearTimeout(idleTimer);
    if (isRunning && !showIdleModal) {
      idleTimer = setTimeout(() => {
        STORE.bot.sailfish.rest();
        isRunning = false;
        showIdleModal = true;
      }, IDLE_LIMIT);
    }
  }

  $effect(() => {
    if (isRunning) {
      window.addEventListener("mousemove", resetIdleTimer);
      window.addEventListener("keydown", resetIdleTimer);
      resetIdleTimer();
    }
    return () => {
      window.removeEventListener("mousemove", resetIdleTimer);
      window.removeEventListener("keydown", resetIdleTimer);
      clearTimeout(idleTimer);
    };
  });

  function resumeStream() {
    showIdleModal = false;
    STORE.bot.sailfish.swim();
    isRunning = true;
    resetIdleTimer();
  }

  const toggleStream = () => {
    if (isRunning) {
      STORE.bot.sailfish.rest();
      isRunning = false;
      clearTimeout(idleTimer);
    } else {
      STORE.bot.sailfish.swim();
      isRunning = true;
      resetIdleTimer();
    }
  };

  onDestroy(() => {
    STORE.bot.sailfish.rest();
    isRunning = false;
    clearTimeout(idleTimer);
  });
</script>

{#if !ready}
  <SolanaShell activePage="streaming">
    <div class="flex items-center justify-center py-24">
      <span class="loading loading-spinner loading-md text-gray-600"></span>
    </div>
  </SolanaShell>
{:else}
<!-- Idle Modal -->
{#if showIdleModal}
  <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
    <div class="max-w-sm w-full bg-gray-950 border border-gray-800 rounded-sm p-8 text-center">
      <div class="flex justify-center mb-6">
        <ClockIcon class="size-10 text-sky-500" />
      </div>
      <h2 class="text-xl font-bold text-white mb-2">Stream paused</h2>
      <p class="text-gray-500 text-sm mb-6">
        The demo stream pauses after 60s of inactivity to save bandwidth.
      </p>
      <div class="flex flex-col gap-3">
        <button
          onclick={resumeStream}
          class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold w-full transition-colors"
        >
          Resume Stream
        </button>
        <a
          href={BUY_URL}
          target="_blank"
          class="btn bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold w-full transition-colors"
        >
          Get Full Access — $75/mo
        </a>
        <p class="text-[10px] text-gray-700 font-mono">Unlimited streaming, no timeouts</p>
      </div>
    </div>
  </div>
{/if}

<SolanaShell
  activePage="streaming"
  {activeTab}
  onTabChange={(tab) => activeTab = tab}
  {isRunning}
  onToggleStream={toggleStream}
>
  {#if hasData || isRunning}
    <!-- Play/Pause (visible once streaming has started) -->
    <div class="flex items-center gap-4 mb-6">
      <button
        onclick={toggleStream}
        class="btn btn-sm {isRunning
          ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-none'
          : 'bg-sky-600 hover:bg-sky-500 text-white border-none'} rounded-sm uppercase tracking-widest font-bold text-xs transition-colors"
      >
        {#if isRunning}
          <OctagonPauseIcon class="size-3.5 me-1.5" />
          Pause
        {:else}
          <PlayIcon class="size-3.5 me-1.5" />
          Stream
        {/if}
      </button>
      {#if isRunning}
        <span class="flex items-center gap-1.5 text-[10px] font-mono text-green-500 uppercase tracking-widest">
          <span class="relative flex h-2 w-2">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          Live
        </span>
      {:else}
        <span class="text-[10px] font-mono text-gray-600 uppercase tracking-widest">Paused</span>
      {/if}
    </div>

    <div class="mb-4">
      <Stats />
    </div>

    {#if activeTab === "Live Trades"}
      <LiveTrades />
    {:else if activeTab === "Top Pools"}
      <TopPools onSelectPool={openPoolView} />
    {:else if activeTab === "Curve Pools"}
      <CurvePools />
    {:else if activeTab === "Wallets"}
      <WalletBubbles />
    {:else if activeTab === "Pool View"}
      <PoolView poolAddress={selectedPool} onBack={() => { activeTab = "Top Pools"; }} />
    {/if}
  {:else}
    <div class="flex flex-col items-center justify-center py-24 gap-6">
      <p class="text-gray-500 text-sm">Live Solana DEX trades, powered by Sailfish</p>
      <button
        onclick={toggleStream}
        class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors px-8 py-3"
      >
        <PlayIcon class="size-4 me-2" />
        Start Streaming
      </button>
      <p class="text-[10px] font-mono text-gray-700">Raydium &middot; PumpFun &middot; PumpSwap &middot; Meteora</p>
    </div>
  {/if}
</SolanaShell>
{/if}
